import { FA_ORDER, type EmotionKey, type Expressions } from '@/components/researcher/liveDemo/constants'
import type { EmotionSeries } from '@/services/emotionAnalysisService'

/** Derived emotion statistics for the analysis panel's stats card. */
export interface EmotionStats {
  hasData: boolean
  /** Time-averaged distribution per emotion, normalised to sum to 1. */
  avg: Record<EmotionKey, number>
  /** Strongest non-neutral emotion (what the stimulus actually evoked). */
  dominant: EmotionKey
  dominantShare: number
  /** Share of non-neutral emotion over time (1 − neutral), 0..1. */
  reactivity: number
  /** Highest instantaneous non-neutral share across the timeline, 0..1. */
  peakReactivity: number
  /** Non-neutral emotions notably present (avg share ≥ 5%), ranked by share. */
  present: EmotionKey[]
  /** How many non-neutral emotions are notably present (= present.length). */
  diversity: number
}

const ZERO: Record<EmotionKey, number> = {
  happy: 0, surprised: 0, neutral: 0, sad: 0, angry: 0, disgusted: 0, fearful: 0,
}

/** Number of non-neutral emotions counted toward "diversity". */
export const DIVERSITY_MAX = FA_ORDER.length - 1

const DIVERSITY_THRESHOLD = 0.05

/**
 * Reduce an emotion series (aggregate average or one respondent's curve) to the
 * headline stats the panel surfaces. Grid points where the curve has no frame
 * (null — leading/trailing gaps) are skipped so they don't dilute the average.
 */
export function computeEmotionStats(series: EmotionSeries | null | undefined): EmotionStats {
  const empty: EmotionStats = {
    hasData: false,
    avg: { ...ZERO },
    dominant: 'happy',
    dominantShare: 0,
    reactivity: 0,
    peakReactivity: 0,
    present: [],
    diversity: 0,
  }
  if (!series) return empty

  const n = series.neutral?.length ?? 0
  const sums: Record<EmotionKey, number> = { ...ZERO }
  let covered = 0
  let peak = 0
  for (let i = 0; i < n; i += 1) {
    // All emotions share the same covered mask — neutral being null marks a gap.
    if (series.neutral[i] == null) continue
    covered += 1
    let nonNeutral = 0
    for (const k of FA_ORDER) {
      const v = series[k]?.[i] ?? 0
      sums[k] += v
      if (k !== 'neutral') nonNeutral += v
    }
    if (nonNeutral > peak) peak = nonNeutral
  }
  if (!covered) return empty

  const mean: Record<EmotionKey, number> = { ...ZERO }
  let total = 0
  for (const k of FA_ORDER) {
    mean[k] = sums[k] / covered
    total += mean[k]
  }
  // Normalise so the donut segments sum to a full ring even if frame vectors
  // didn't sum to exactly 1.
  const avg: Record<EmotionKey, number> = { ...ZERO }
  for (const k of FA_ORDER) avg[k] = total > 0 ? mean[k] / total : 0

  let dominant: EmotionKey = 'happy'
  let dominantShare = -1
  for (const k of FA_ORDER) {
    if (k === 'neutral') continue
    if (avg[k] > dominantShare) {
      dominant = k
      dominantShare = avg[k]
    }
  }
  // Non-neutral emotions that crossed the threshold, strongest first — drives
  // both the diversity count and the per-emotion meter in the panel.
  const present = FA_ORDER.filter(
    (k) => k !== 'neutral' && avg[k] >= DIVERSITY_THRESHOLD,
  ).sort((a, b) => avg[b] - avg[a])

  return {
    hasData: true,
    avg,
    dominant,
    dominantShare: Math.max(0, dominantShare),
    reactivity: Math.max(0, Math.min(1, 1 - avg.neutral)),
    peakReactivity: Math.max(0, Math.min(1, peak)),
    present,
    diversity: present.length,
  }
}

/** Instantaneous emotion distribution at one point on the timeline. */
export interface MomentStats {
  /** False when the playhead sits on a gap (no analysed frame at this instant). */
  hasData: boolean
  /** Frame distribution per emotion, normalised to sum to 1. */
  avg: Record<EmotionKey, number>
  /** Strongest non-neutral emotion at this instant. */
  dominant: EmotionKey
  /** Non-neutral share at this instant (1 − neutral), 0..1. */
  reactivity: number
}

/**
 * Sample the emotion series at a single playhead position — picks the grid point
 * nearest `currentMs` and normalises that frame's vector. Powers the stats card's
 * "moment" mode, which tracks the video playhead instead of averaging the whole clip.
 */
export function computeMomentStats(
  series: EmotionSeries | null | undefined,
  gridMs: number[] | null | undefined,
  currentMs: number,
): MomentStats {
  const empty: MomentStats = { hasData: false, avg: { ...ZERO }, dominant: 'happy', reactivity: 0 }
  if (!series || !gridMs || gridMs.length === 0) return empty

  // Nearest grid index to the playhead (grids are short — a linear scan is fine).
  let idx = 0
  let best = Infinity
  for (let i = 0; i < gridMs.length; i += 1) {
    const d = Math.abs(gridMs[i] - currentMs)
    if (d < best) {
      best = d
      idx = i
    }
  }
  // A null neutral marks a gap with no analysed frame at this instant.
  if (series.neutral?.[idx] == null) return empty

  const raw: Record<EmotionKey, number> = { ...ZERO }
  let total = 0
  for (const k of FA_ORDER) {
    const v = Math.max(0, series[k]?.[idx] ?? 0)
    raw[k] = v
    total += v
  }
  const avg: Record<EmotionKey, number> = { ...ZERO }
  for (const k of FA_ORDER) avg[k] = total > 0 ? raw[k] / total : 0

  let dominant: EmotionKey = 'happy'
  let dominantShare = -1
  for (const k of FA_ORDER) {
    if (k === 'neutral') continue
    if (avg[k] > dominantShare) {
      dominant = k
      dominantShare = avg[k]
    }
  }

  return { hasData: true, avg, dominant, reactivity: Math.max(0, Math.min(1, 1 - avg.neutral)) }
}

/** Qualitative reactivity band for labelling/colour. */
export function reactivityBand(reactivity: number): 'low' | 'medium' | 'high' {
  if (reactivity >= 0.45) return 'high'
  if (reactivity >= 0.2) return 'medium'
  return 'low'
}

/** Qualitative diversity band for labelling — neutral-valenced (out of 6). */
export function diversityBand(diversity: number): 'low' | 'medium' | 'high' {
  if (diversity >= 4) return 'high'
  if (diversity >= 2) return 'medium'
  return 'low'
}

/** Axis order around the radar, clockwise from the top. Neutral is left out on
 *  purpose: it is most of almost every frame and would flatten the rest. */
export const RADAR_AXES: EmotionKey[] = ['happy', 'surprised', 'fearful', 'sad', 'disgusted', 'angry']

/** Share of each non-neutral emotion among the non-neutral ones (sums to 1),
 *  or null when the frame has no non-neutral signal at all. */
export function radarShares(vec: Expressions | null): Record<EmotionKey, number> | null {
  if (!vec) return null
  const total = RADAR_AXES.reduce((s, k) => s + Math.max(0, vec[k] ?? 0), 0)
  if (total <= 0) return null
  const out = { happy: 0, surprised: 0, neutral: 0, sad: 0, angry: 0, disgusted: 0, fearful: 0 }
  for (const k of RADAR_AXES) out[k] = Math.max(0, vec[k] ?? 0) / total
  return out
}
